import React, { useMemo, useState } from 'react';
import { Casa, Tarea } from '../../types';
import { useTareas } from '../../context/TareasContext';
import { useAuth } from '../../context/AuthContext';
import { useConfirmar } from '../../context/ConfirmarContext';
import { describirRepeticion } from '../../utils/tareas';
import { Check, Copy } from 'lucide-react';
import { MensajeError, ModalBase, botonSecundario, chip } from './ui';
import { vibrar } from '../../utils/hapticos';

const clave = (t: Tarea) => `${t.casaId}:${t.nombre.trim().toLowerCase()}`;

/**
 * Elegir varias tareas y borrarlas juntas. Salen todas, también las pausadas
 * y las que no caen en la semana que estás viendo, ordenadas por nombre para
 * que las repetidas queden juntas. En una casa compartida solo se pueden
 * elegir las que creaste tú, o todas si la organizas (como dicen las reglas).
 */
export const ModalBorrarTareas: React.FC<{ casas: Casa[]; onClose: () => void }> = ({ casas, onClose }) => {
  const { user } = useAuth();
  const { tareas, eliminarTareas } = useTareas();
  const confirmar = useConfirmar();
  const [elegidas, setElegidas] = useState<Set<string>>(() => new Set());
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState('');

  const idsCasas = new Set(casas.map(c => c.id));
  const casaPorId = new Map(casas.map(c => [c.id, c]));
  const sePuede = (t: Tarea) => {
    const casa = casaPorId.get(t.casaId);
    return !!casa && (casa.personal || casa.organizadoresUids.includes(user?.uid ?? '') || t.creadoPorUid === user?.uid);
  };

  const grupos = useMemo(() => casas
    .map(casa => ({
      casa,
      lista: tareas
        .filter(t => t.casaId === casa.id)
        .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es') || a.createdAt.getTime() - b.createdAt.getTime()),
    }))
    .filter(g => g.lista.length > 0),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [tareas, casas]);

  const visibles = grupos.flatMap(g => g.lista).filter(sePuede);
  // Repetidas: de cada nombre en una casa se queda la más antigua
  const repetidas = useMemo(() => {
    const vistas = new Set<string>();
    return visibles.filter(t => {
      const k = clave(t);
      if (vistas.has(k)) return true;
      vistas.add(k);
      return false;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tareas, casas]);

  const alternar = (id: string) => setElegidas(prev => {
    const s = new Set(prev);
    if (s.has(id)) s.delete(id); else s.add(id);
    return s;
  });
  const todasElegidas = visibles.length > 0 && visibles.every(t => elegidas.has(t.id));
  const lista = tareas.filter(t => elegidas.has(t.id) && idsCasas.has(t.casaId) && sePuede(t));

  const borrar = async () => {
    if (!lista.length) return;
    if (!(await confirmar({
      titulo: lista.length === 1 ? `¿Borrar «${lista[0].nombre}»?` : `¿Borrar ${lista.length} tareas?`,
      mensaje: 'Desaparecen de la semana con todas sus repeticiones. Lo que ya se hizo sigue en el historial.',
      accion: 'Borrar', destructiva: true,
    }))) return;
    setBorrando(true);
    setError('');
    try {
      await eliminarTareas(lista);
      vibrar.fuerte();
      onClose();
    } catch (e) {
      console.error(e);
      vibrar.error();
      setError('No se han podido borrar. Inténtalo de nuevo.');
      setBorrando(false);
    }
  };

  return (
    <ModalBase titulo="Borrar tareas" onClose={onClose}>
      <div className="p-5 space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Marca las que quieres quitar. Salen todas, también las pausadas, ordenadas por nombre.
        </p>
        <MensajeError texto={error} />

        {visibles.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No hay tareas que puedas borrar aquí.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            <button type="button" onClick={() => setElegidas(todasElegidas ? new Set() : new Set(visibles.map(t => t.id)))}
              className={chip(todasElegidas)}>
              {todasElegidas ? 'Quitar todas' : 'Elegir todas'}
            </button>
            {repetidas.length > 0 && (
              <button type="button" onClick={() => setElegidas(new Set(repetidas.map(t => t.id)))}
                className={`${chip(false)} inline-flex items-center gap-1`}>
                <Copy className="w-3 h-3" /> Elegir las repetidas ({repetidas.length})
              </button>
            )}
          </div>
        )}

        {grupos.map(({ casa, lista: delGrupo }) => (
          <section key={casa.id}>
            {grupos.length > 1 && (
              <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5">{casa.icono} {casa.nombre}</h3>
            )}
            <ul className="divide-y divide-gray-100 dark:divide-gray-700 rounded-xl border border-gray-200 dark:border-gray-700">
              {delGrupo.map(t => {
                const puede = sePuede(t);
                const marcada = elegidas.has(t.id);
                return (
                  <li key={t.id}>
                    <button type="button" onClick={() => alternar(t.id)} disabled={!puede} aria-pressed={marcada}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50
                                 transition disabled:opacity-50 disabled:hover:bg-transparent">
                      <span className={`w-5 h-5 shrink-0 rounded-md border flex items-center justify-center ${
                        marcada ? 'bg-red-600 border-red-600 text-white' : 'border-gray-300 dark:border-gray-600'}`}>
                        {marcada && <Check className="w-3.5 h-3.5" />}
                      </span>
                      <span className="text-xl" aria-hidden>{t.icono}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-gray-900 dark:text-white truncate">{t.nombre}</span>
                        <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                          {describirRepeticion(t.repeticion, t.fecha)}
                          {!t.activa && ' · En pausa'}
                          {!puede && ' · La creó otra persona'}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}

        <div className="sticky bottom-0 -mx-5 -mb-5 px-5 py-3 flex gap-2 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          <button onClick={borrar} disabled={borrando || lista.length === 0}
            className="flex-1 py-2.5 inline-flex items-center justify-center text-sm font-semibold rounded-lg
                       bg-red-600 hover:bg-red-700 text-white transition disabled:opacity-50">
            {borrando ? 'Borrando…' : lista.length ? `Borrar ${lista.length} ${lista.length === 1 ? 'tarea' : 'tareas'}` : 'Borrar'}
          </button>
          <button onClick={onClose} className={`px-5 py-2.5 ${botonSecundario}`}>Cancelar</button>
        </div>
      </div>
    </ModalBase>
  );
};
